// ─────────────────────────────────────────────
//  Add Facility modal — create a new facility from Facility Settings.
//
//  Captures name, address, and contact-person info, plus two setup options:
//    • copy surgeons from an existing facility (pick which), and
//    • use the shared anesthesia staff pool (yes/no).
//  Locations and specialties start empty so the new facility is a clean shell.
// ─────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import {
  createFacility, addSurgeon, addProcedureRef, listSurgeonsForFacility,
} from "../firebase/firestore";
import { useToast } from "../context/ToastContext";
import { slugify } from "../domain/locations";
import Modal from "../components/Modal";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={s.label}>
        {label}{required && <span style={{ color: color.accent.error }} aria-hidden="true"> *</span>}
      </label>
      {children}
    </div>
  );
}

// Pick a facility id that doesn't collide with an existing one.
function uniqueId(name, existingIds) {
  const base = slugify(name);
  if (!existingIds.includes(base)) return base;
  let n = 2;
  while (existingIds.includes(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export function AddFacilityModal({ isOpen, onClose, facilities, onCreated }) {
  const toast = useToast();
  const blank = {
    name: "", address: "",
    contact: { name: "", email: "", phone: "" },
    copySurgeons: false, copyFrom: "",
    sharedStaff: true,
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const existingIds = useMemo(() => facilities.map((f) => f.id), [facilities]);
  const copySources = useMemo(() => facilities.filter((f) => f.id !== "__new__"), [facilities]);

  const set = (path, value) => setForm((p) => {
    if (!path.includes(".")) return { ...p, [path]: value };
    const [g, k] = path.split(".");
    return { ...p, [g]: { ...p[g], [k]: value } };
  });

  const close = () => { setForm(blank); onClose(); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Facility name is required."); return; }
    if (!form.address.trim()) { toast.error("Address is required."); return; }
    if (!form.contact.name.trim() || !form.contact.email.trim() || !form.contact.phone.trim()) {
      toast.error("Contact person name, email, and phone are required."); return;
    }
    if (form.copySurgeons && !form.copyFrom) { toast.error("Pick a facility to copy surgeons from."); return; }

    setBusy(true);
    const id = uniqueId(form.name, existingIds);
    try {
      await createFacility(id, {
        name: form.name.trim(),
        address: form.address.trim(),
        contact: form.contact,
        sharedStaff: form.sharedStaff,
        isActive: true,
      });

      // ⚠️ COPIES THE SURGEON RECORDS, NOT THE RETIRED ARRAY FIELD. This used
      // to clone procedure_refs.surgeonList[], which nothing reads any more —
      // the new facility would have looked configured and shown an empty
      // Surgeons section on the Team page. Each record is re-created under the
      // new facilityId, and its name is mirrored into the new facility's
      // assignment typeahead the same way the Setup panel does it.
      if (form.copySurgeons && form.copyFrom) {
        const rows = await listSurgeonsForFacility(form.copyFrom);
        for (const sg of rows) {
          const name = String(sg.name || "").trim();
          if (!name) continue;
          await addSurgeon({
            name,
            phoneNumber: sg.phoneNumber || "",
            phoneDisplay: sg.phoneDisplay || "",
            email: sg.email || "",
            specialty: sg.specialty || "",
            listed: sg.listed !== false,
            facilityId: id,
          });
          await addProcedureRef(id, "surgeons", name);
        }
      }

      toast.success(`"${form.name.trim()}" created.`);
      onCreated(id);
      close();
    } catch (err) {
      console.error("Create facility failed:", err);
      toast.error("Could not create facility. Admin rights are required.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title="Add Facility" maxWidth={560}
      footer={
        <>
          <button style={s.outlineBtn} onClick={close} disabled={busy}>Cancel</button>
          <button style={s.primaryBtn} onClick={save} disabled={busy}>{busy ? "Creating…" : "Create Facility"}</button>
        </>
      }>
      <Field label="Facility Name" required>
        <input style={s.input} value={form.name} autoFocus
          onChange={(e) => set("name", e.target.value)} />
      </Field>
      <Field label="Address" required>
        <input style={s.input} value={form.address} onChange={(e) => set("address", e.target.value)} />
      </Field>

      <div style={{ fontSize: 12, fontWeight: 700, color: color.text.secondary, margin: "4px 0 8px" }}>Contact Person</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {["name", "email", "phone"].map((k) => (
          <Field key={k} label={k[0].toUpperCase() + k.slice(1)} required>
            <input style={s.input} value={form.contact[k]}
              type={k === "email" ? "email" : k === "phone" ? "tel" : "text"}
              onChange={(e) => set(`contact.${k}`, e.target.value)} />
          </Field>
        ))}
      </div>

      <div style={{ borderTop: `1px solid ${color.border.default}`, margin: "8px 0 14px" }} />

      <Field label="Copy surgeons from an existing facility?">
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={form.copySurgeons}
              onChange={(e) => set("copySurgeons", e.target.checked)} />
            Yes, copy from
          </label>
          <select style={{ ...s.select, width: 240 }} value={form.copyFrom}
            disabled={!form.copySurgeons}
            onChange={(e) => set("copyFrom", e.target.value)}>
            <option value="">Select facility…</option>
            {copySources.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </div>
      </Field>

      <Field label="Use the shared anesthesia staff pool?">
        <div style={{ display: "flex", gap: 16 }}>
          {[["true", "Yes — shared pool"], ["false", "No — separate"]].map(([v, lbl]) => (
            <label key={v} style={{ fontSize: 13, display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input type="radio" name="sharedStaff" checked={form.sharedStaff === (v === "true")}
                onChange={() => set("sharedStaff", v === "true")} />
              {lbl}
            </label>
          ))}
        </div>
      </Field>

      <p style={{ fontSize: 12, color: color.text.muted, marginBottom: 0 }}>
        Locations and specialties start empty — configure them after the facility is created.
      </p>
    </Modal>
  );
}
